import { UseQueryResult, queryOptions, useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { usePolling } from '../../pages/AdminPanel/BulkDealDocsUpload/usePolling';
import { KpiPeriodRequestDocument } from '../../schemas/KpiPeriodRequestDocument.schema';
import {
  KpiDocsExportJob,
  KpiDocsExportParams,
  getKpiDocsExportStatus,
  getKpiPeriodRequestDocuments,
  initiateKpiDocsExport,
} from '../queries/KpiPeriodRequestDocumentQueries';

/** Query key factory for type safety and consistency */
export const kpiPeriodRequestDocsKeys = {
  all: ['kpi-period-request-docs'] as const,
  byCompany: (companyId?: number | null) => [...kpiPeriodRequestDocsKeys.all, companyId] as const,
};

/** Query options factory - usable with useQuery, useSuspenseQuery, or prefetching */
export const kpiPeriodRequestDocumentsQueryOptions = (companyId?: number | null) =>
  queryOptions({
    queryKey: kpiPeriodRequestDocsKeys.byCompany(companyId),
    queryFn: () => getKpiPeriodRequestDocuments(companyId!),
    enabled: Boolean(companyId),
  });

export function useKpiPeriodRequestDocuments(
  companyId?: number | null
): UseQueryResult<KpiPeriodRequestDocument[], Error> {
  return useQuery(kpiPeriodRequestDocumentsQueryOptions(companyId));
}

export type KpiDocsExportState =
  | { phase: 'idle' }
  | { phase: 'initiating' }
  | { phase: 'polling'; jobId: string }
  | { phase: 'complete'; downloadUrl: string }
  | { phase: 'empty' }
  | { phase: 'failed'; errorMessage: string | null }
  | { phase: 'error'; message: string };

export function useKpiDocsExport(params?: KpiDocsExportParams) {
  const [exportState, setExportState] = useState<KpiDocsExportState>({ phase: 'idle' });

  const isPolling = exportState.phase === 'polling';
  const jobId = exportState.phase === 'polling' ? exportState.jobId : null;

  // Tracks whether the current polling session should stop. Resetting this to
  // false before enabling polling ensures the stale-data early-exit inside
  // usePolling doesn't prevent a second export from starting after the first
  // one completed.
  const shouldStopRef = useRef(false);

  const pollFn = useCallback(async () => {
    if (!jobId) {
      throw new Error('No export job ID');
    }
    return getKpiDocsExportStatus(jobId);
  }, [jobId]);

  const onSuccess = useCallback((data: KpiDocsExportJob) => {
    if (data.status === 'complete') {
      shouldStopRef.current = true;
      if (data.downloadUrl) {
        setExportState({ phase: 'complete', downloadUrl: data.downloadUrl });
      } else {
        setExportState({
          phase: 'failed',
          errorMessage: 'Export completed but download URL is unavailable.',
        });
      }
    } else if (data.status === 'empty') {
      shouldStopRef.current = true;
      setExportState({ phase: 'empty' });
    } else if (data.status === 'failed') {
      shouldStopRef.current = true;
      setExportState({ phase: 'failed', errorMessage: data.errorMessage ?? null });
    }
  }, []);

  const onError = useCallback((error: Error) => {
    shouldStopRef.current = true;
    setExportState({ phase: 'error', message: error.message });
  }, []);

  // Reads shouldStopRef rather than inspecting `data` directly — this avoids
  // stale terminal data from a prior export short-circuiting shouldContinue.
  const shouldContinue = useCallback((_data: KpiDocsExportJob | null) => !shouldStopRef.current, []);

  usePolling({
    pollFn,
    onSuccess,
    onError,
    shouldContinue,
    enabled: isPolling,
    initialInterval: 3_000,
    maxInterval: 15_000,
  });

  const initiateExport = useCallback(async () => {
    shouldStopRef.current = false;
    setExportState({ phase: 'initiating' });
    try {
      const job = await initiateKpiDocsExport(params);
      setExportState({ phase: 'polling', jobId: job.jobId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start export';
      setExportState({ phase: 'error', message });
    }
  }, [params]);

  const reset = useCallback(() => setExportState({ phase: 'idle' }), []);

  return { exportState, initiateExport, reset };
}
